import React, { useContext, useState } from "react"
import { MobergColumn, MobergRow } from "../../../../../Moberg"
import { ConfigureWindowModalContext } from "../ConfigureWindowModal"
import { CompositePartTraceConfigJSON, RenderStrategy, SpectrogramTraceConfigJSON, TraceConfigJSON, UpdateTraceOptions } from "../../../../../Pages/Data/Visualize/DataReview/Types/Trace"
import { ModalityGraphConfigJSON } from "../../../../../Pages/Data/Visualize/DataReview/Types/Graph"
import { ColorSpectrum } from "../../../../../Pages/Data/Visualize/DataReview/Types/ColorSpectrum"
import { applyModalitySpecificDefaults } from "../../../../../Constants/LabelSettings"
import { AnalyticSpectrogramModalities } from "./AnalyticSpectrogramModalities"
import { SourceSpectrogramModalities } from "./SourceSpectrogramModalities"
import { SpectrogramGraphModalitiesContext } from "./SpectrogramTraceSettings"
import { TabCount } from "../LineGraphModalities/TabCount"
import { useEnvironmentVariablesProvider } from "../../../../../Providers/EnvironmentVariablesProvider"

export const SpectrogramGraphModalities: React.FC = () => {
	const { activeSourceTraces, activeAnalyticTraces, updateGraph, setValidationErrors, removeTrace } = useContext(ConfigureWindowModalContext)
	const { environmentVariables } = useEnvironmentVariablesProvider()

	// Pre-computed (offline) DSA source traces exist only behind the feature flag;
	// without it the modal keeps its single Analytics tab.
	const tabs = environmentVariables.FEATURE_FLAG_QEEG_DSA_SOURCE ? ["Source data", "Analytics"] : ["Analytics"]
	const [selectedTab, setSelectedTab] = useState("Analytics")

	const activeSourceSpectrogramTraces = (activeSourceTraces ?? []).filter(
		(trace: TraceConfigJSON): trace is SpectrogramTraceConfigJSON => trace.renderStrategy === RenderStrategy.SPECTROGRAM
	)
	const activeAnalyticSpectrogramTraces = (activeAnalyticTraces ?? []).filter(
		(trace): trace is SpectrogramTraceConfigJSON => trace.renderStrategy === RenderStrategy.SPECTROGRAM
	)

	const handleValidationErrorChange = (key: string, error: string | undefined) => {
		setValidationErrors(previous => {
			const copy = new Map(previous)

			if (copy.has(key) && error === undefined) {
				copy.delete(key)
			} else if (error !== undefined) {
				copy.set(key, error)
			}

			return copy
		})
	}

	const updateTrace = (traceId: string, config: Partial<SpectrogramTraceConfigJSON>, options?: UpdateTraceOptions) => {
		updateGraph((previousGraph: ModalityGraphConfigJSON) => {
			const defaults = { isVisible: true }
			const newTraces: TraceConfigJSON[] = (previousGraph.traces ?? []).map((trace: TraceConfigJSON) => {
				if (traceId === trace.id) {
					let newTrace = { ...defaults, ...trace, ...config, id: traceId }

					if (options?.useModalitySpecificDefaults) {
						newTrace = applyModalitySpecificDefaults(newTrace, newTrace.rawDataKey, RenderStrategy.SPECTROGRAM)
					}

					if ((config as CompositePartTraceConfigJSON).isCompositePart && (config as CompositePartTraceConfigJSON).compositeIndex !== undefined) {
						(newTrace as CompositePartTraceConfigJSON).compositeIndex = (config as CompositePartTraceConfigJSON).compositeIndex
					}

					return newTrace as TraceConfigJSON
				}

				return trace
			})

			return { ...previousGraph, traces: newTraces }
		})
	}

	const handleColorSpectrumChange = (colorSpectrum: ColorSpectrum, traceConfig: SpectrogramTraceConfigJSON) => {
		updateTrace(traceConfig.id, { ...traceConfig, colorSpectrum })
	}

	const handleDelete = (id: string) => {
		removeTrace(id)

		handleValidationErrorChange(`${id}-lowerBound`, undefined)
		handleValidationErrorChange(`${id}-upperBound`, undefined)
	}

	const TabContent = () => {
		switch (selectedTab) {
			case "Source data":
				return <SourceSpectrogramModalities />
			case "Analytics":
				return <AnalyticSpectrogramModalities />
			default:
				throw new Error("Tab section not implemented! " + selectedTab)
		}
	}

	return (
		<MobergColumn gap="16px">
			<div style={{ borderBottom: "1px solid var(--color-border-light)" }}>
				<MobergRow gap="24px" style={{ padding: "0 4px" }}>
					{tabs.map(tab => (
						<div
							key={tab}
							onClick={() => setSelectedTab(tab)}
							style={{
								cursor: "pointer",
								padding: "8px 4px",
								fontWeight: tab === selectedTab ? 600 : 400,
								color: tab === selectedTab ? "var(--color-accent-interactive)" : "var(--text-secondary-color)",
								borderBottom: tab === selectedTab ? "2px solid var(--color-accent-interactive)" : "2px solid transparent",
								transition: "color 0.15s, border-bottom 0.15s",
								fontSize: 14,
								background: "none",
								outline: "none",
								display: "flex",
								alignItems: "center",
								gap: "6px",
								marginBottom: "-1px",
							}}
						>
							<span>{tab}</span>
							<TabCount tab={tab} />
						</div>
					))}
				</MobergRow>
			</div>

			<SpectrogramGraphModalitiesContext.Provider
				value={{
					activeSourceSpectrogramTraces,
					activeAnalyticSpectrogramTraces,
					handleDelete,
					handleColorSpectrumChange,
					handleValidationErrorChange,
					updateTrace,
				}}
			>
				{TabContent()}
			</SpectrogramGraphModalitiesContext.Provider>
		</MobergColumn>
	)
}
